import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Globe,
  MapPin,
  Layers,
  Users,
  Factory,
  Shield,
  Coins,
  AlertTriangle,
} from 'lucide-react';
import backendApi from '../../services/backendApi';
import { ModuleCatalogItem, loadModuleCatalog } from '../../utils/moduleAccessAdmin';

interface CompanyDetailModel {
  company_id: number;
  company_name: string;
  company_code: string;
  industry_type?: string | null;
  country?: string | null;
  location?: string | null;
  timezone?: string | null;
  currency?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | string[] | null;
  primary_contact_phones?: string[];
  subscription_plan?: string | null;
  max_sites: number;
  max_users: number;
  is_active: boolean;
  is_trial: boolean;
  logo_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface CompanyTokenSummary {
  total_company_issued: number;
  mapped_to_ho: number;
  pending_tokens: number;
  ho_admin_required?: boolean;
  tab_tokens: {
    download_raw_data: number;
    generate_alerts: number;
    run_rtc_optimization: number;
  };
}

const parsePhoneValues = (value: string | string[] | null | undefined, listValue?: string[]): string[] => {
  if (Array.isArray(listValue) && listValue.length > 0) {
    return listValue.map((item) => String(item).trim()).filter((item) => item.length > 0);
  }

  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter((item) => item.length > 0);
  }

  const text = String(value || '').trim();
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter((item) => item.length > 0);
    }
  } catch (error) {
    // Fallback for legacy comma-separated value
  }

  return text
    .split(/[,;/|]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
};

const CompanyDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [company, setCompany] = useState<CompanyDetailModel | null>(null);
  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [siteCount, setSiteCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [tokenSummary, setTokenSummary] = useState<CompanyTokenSummary | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Company ID missing');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [companyResponse, subscriptionsResponse, modules, sitesResponse, usersResponse, tokenResponse] = await Promise.all([
          backendApi.getCompany(id),
          backendApi.getNavigationSubscriptions(id),
          loadModuleCatalog(backendApi),
          backendApi.getSites(false, '', id),
          backendApi.getUsers(id),
          backendApi.getAdminCompanyTokens(id),
        ]);

        if (!companyResponse.success || !companyResponse.company) {
          setError(companyResponse.error || 'Failed to load company details');
          return;
        }

        setCompany(companyResponse.company);
        setSubscriptions(Array.isArray(subscriptionsResponse?.subscriptions) ? subscriptionsResponse.subscriptions : []);
        setModuleCatalog(Array.isArray(modules) ? modules : []);
        setSiteCount(Array.isArray(sitesResponse?.sites) ? sitesResponse.sites.length : 0);
        setUserCount(Array.isArray(usersResponse?.users) ? usersResponse.users.length : 0);
        if (tokenResponse?.success && tokenResponse?.summary) {
          setTokenSummary(tokenResponse.summary as CompanyTokenSummary);
        } else {
          setTokenSummary(null);
        }
      } catch (loadError: any) {
        setError(loadError?.message || 'Unable to load company details');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [id]);

  const moduleSummary = useMemo(() => {
    const active = subscriptions.filter((item) => String(item?.status || '').toLowerCase() === 'active');
    const activeSet = new Set(active.map((item) => item.module_key));
    const allKeys = moduleCatalog.map((item) => item.module_key);

    return {
      assignedCount: activeSet.size,
      totalCount: allKeys.length,
      assignedModules: moduleCatalog.filter((item) => activeSet.has(item.module_key)),
      unassignedModules: moduleCatalog.filter((item) => !activeSet.has(item.module_key)),
    };
  }, [moduleCatalog, subscriptions]);

  const phones = parsePhoneValues(company?.primary_contact_phone, company?.primary_contact_phones);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-shell">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading company details...</p>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
        <div className="max-w-5xl mx-auto admin-section">
          <div className="admin-section-body py-16 text-center">
            <p className="text-[#B42318] font-semibold">{error || 'Company not found'}</p>
            <button
              onClick={() => navigate('/admin/companies')}
              className="admin-button admin-button-primary mt-6"
            >
              <ArrowLeft size={16} />
              Back to Companies
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="admin-hero p-4 sm:p-4 lg:p-5">
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">Customer profile</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-white border items-center justify-center overflow-hidden shadow-sm flex-shrink-0">
                  {company.logo_url ? (
                    <img src={company.logo_url} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Building2 size={22} className="text-[#0084CF]" />
                  )}
                </div>
                <div>
                  <h1 className="admin-page-title">{company.company_name}</h1>
                  <p className="admin-page-subtitle mt-2">Code: {company.company_code} • Plan: {(company.subscription_plan || 'basic').toUpperCase()}</p>
                </div>
              </div>
            </div>

            <button onClick={() => navigate('/admin/companies')} className="admin-button admin-button-ghost">
              <ArrowLeft size={16} />
              Back to Companies
            </button>
          </div>

          <div className="admin-stat-strip mt-6">
            <div className="admin-stat-card">
              <p className="admin-stat-label">Assigned Modules</p>
              <p className="admin-stat-value">{moduleSummary.assignedCount}/{moduleSummary.totalCount}</p>
              <p className="admin-stat-hint">Currently active subscriptions</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Sites</p>
              <p className="admin-stat-value">{siteCount}</p>
              <p className="admin-stat-hint">Sites mapped to this company</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Users</p>
              <p className="admin-stat-value">{userCount}</p>
              <p className="admin-stat-hint">Users under this company scope</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Status</p>
              <p className="admin-stat-value">{company.is_active ? 'Active' : 'Inactive'}</p>
              <p className="admin-stat-hint">Trial: {company.is_trial ? 'Enabled' : 'Disabled'}</p>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Company Information</h2>
              <p className="admin-section-copy">Core customer profile and operational limits.</p>
            </div>
          </div>
          <div className="admin-section-body grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div className="admin-row">
              <div className="admin-row-icon"><Factory size={18} /></div>
              <div>
                <p className="admin-meta-label">Industry</p>
                <p className="admin-meta-value">{company.industry_type || 'Not specified'}</p>
              </div>
            </div>
            <div className="admin-row">
              <div className="admin-row-icon"><Globe size={18} /></div>
              <div>
                <p className="admin-meta-label">Country / Currency</p>
                <p className="admin-meta-value">{company.country || 'N/A'} • {company.currency || 'N/A'}</p>
              </div>
            </div>
            <div className="admin-row">
              <div className="admin-row-icon"><MapPin size={18} /></div>
              <div>
                <p className="admin-meta-label">Location / Timezone</p>
                <p className="admin-meta-value">{company.location || 'N/A'} • {company.timezone || 'N/A'}</p>
              </div>
            </div>
            <div className="admin-row">
              <div className="admin-row-icon"><Users size={18} /></div>
              <div>
                <p className="admin-meta-label">Capacity</p>
                <p className="admin-meta-value">{company.max_sites} sites • {company.max_users} users</p>
              </div>
            </div>
            <div className="admin-row">
              <div className="admin-row-icon">{company.is_active ? <CheckCircle size={18} /> : <XCircle size={18} />}</div>
              <div>
                <p className="admin-meta-label">Lifecycle</p>
                <p className="admin-meta-value">{company.is_active ? 'Enabled customer' : 'Disabled customer'}</p>
              </div>
            </div>
            <div className="admin-row">
              <div className="admin-row-icon"><Shield size={18} /></div>
              <div>
                <p className="admin-meta-label">Trial</p>
                <p className="admin-meta-value">{company.is_trial ? 'Trial active' : 'Paid / non-trial'}</p>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Primary Contact</h2>
              <p className="admin-section-copy">All registered contact details for this customer company.</p>
            </div>
          </div>
          <div className="admin-section-body grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="admin-row">
              <div className="admin-row-icon"><Users size={18} /></div>
              <div>
                <p className="admin-meta-label">Name</p>
                <p className="admin-meta-value">{company.primary_contact_name || 'Not provided'}</p>
              </div>
            </div>
            <div className="admin-row">
              <div className="admin-row-icon"><Mail size={18} /></div>
              <div>
                <p className="admin-meta-label">Email</p>
                <p className="admin-meta-value">{company.primary_contact_email || 'Not provided'}</p>
              </div>
            </div>
            <div className="admin-row">
              <div className="admin-row-icon"><Phone size={18} /></div>
              <div>
                <p className="admin-meta-label">Phone Numbers</p>
                <p className="admin-meta-value">{phones.length > 0 ? phones.join(' • ') : 'Not provided'}</p>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Module Assignment</h2>
              <p className="admin-section-copy">Assigned module subscriptions for this customer company.</p>
            </div>
            <div className="admin-pill admin-pill-muted !font-bold">
              {moduleSummary.assignedCount}/{moduleSummary.totalCount} assigned
            </div>
          </div>
          <div className="admin-section-body">
            {moduleSummary.totalCount === 0 ? (
              <div className="admin-empty">No module catalog available for this company context.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {moduleCatalog.map((module) => {
                  const isAssigned = moduleSummary.assignedModules.some((item) => item.module_key === module.module_key);
                  return (
                    <div key={module.module_key} className={`rounded-xl border px-4 py-3 ${isAssigned ? 'border-[#0084CF]/30 bg-[#EEF5FF]' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="admin-module-card-title text-sm text-[#0B1A2F] truncate">{module.module_name}</p>
                          <p className="admin-module-card-key text-xs tracking-[0.02em] text-[#5B708B]">{module.module_key}</p>
                        </div>
                        <span className={`admin-pill !font-bold shrink-0 ${isAssigned ? 'admin-pill-success' : 'admin-pill-muted'}`}>
                          {isAssigned ? 'Assigned' : 'Not assigned'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Simulation Token Assignment (Tab-wise)</h2>
              <p className="admin-section-copy">Tokens assigned from customer creation form across each simulation tab.</p>
            </div>
            <div className="admin-pill admin-pill-muted !font-bold">Created-time allocation view</div>
          </div>

          <div className="admin-section-body space-y-4">
            {!tokenSummary ? (
              <div className="admin-empty">No token assignment data available for this company yet.</div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                  <div className="rounded-xl border border-[#C2DFF1] bg-white px-4 py-3">
                    <p className="admin-token-metric-title text-[10px] uppercase tracking-[0.04em] text-[#1C4D88]">Company Issued</p>
                    <p className="admin-token-metric-copy text-[11px] text-[#1C4D88]/80 mt-1">Total seeded from company form</p>
                    <p className="text-2xl font-black text-[#0D2747] mt-2">{Number(tokenSummary.total_company_issued || 0)}</p>
                  </div>

                  <div className="rounded-xl border border-[#C2DFF1] bg-white px-4 py-3">
                    <p className="admin-token-metric-title text-[10px] uppercase tracking-[0.04em] text-[#1C4D88]">Mapped To HO</p>
                    <p className="admin-token-metric-copy text-[11px] text-[#1C4D88]/80 mt-1">Visible in HO admin wallet</p>
                    <p className="text-2xl font-black text-[#0D2747] mt-2">{Number(tokenSummary.mapped_to_ho || 0)}</p>
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-white px-4 py-3">
                    <p className="admin-token-metric-title text-[10px] uppercase tracking-[0.04em] text-amber-700">Pending (No HO)</p>
                    <p className="admin-token-metric-copy text-[11px] text-amber-700/80 mt-1">Will map after HO creation</p>
                    <p className="text-2xl font-black text-amber-900 mt-2">{Number(tokenSummary.pending_tokens || 0)}</p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <p className="admin-token-metric-title text-[10px] uppercase tracking-[0.04em] text-gray-700">Total Tabs Tracked</p>
                    <p className="admin-token-metric-copy text-[11px] text-gray-600 mt-1">Simulation tabs receiving tokens</p>
                    <p className="text-2xl font-black text-gray-900 mt-2">3</p>
                  </div>
                </div>

                {tokenSummary.ho_admin_required && (
                  <div className="rounded-lg border border-amber-300 bg-amber-100 px-4 py-3 flex items-start gap-2">
                    <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
                    <p className="text-sm font-semibold text-amber-900">
                      Pending tokens will become visible and usable once an HO Admin user is available for this company.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <p className="admin-token-tab-title text-xs text-[#0B1A2F]">Download Raw Data</p>
                    <p className="admin-token-tab-copy text-[10px] text-[#5B708B] mt-1">Assigned from form</p>
                    <p className="text-2xl font-black text-[#0084CF] mt-1">{Number(tokenSummary.tab_tokens?.download_raw_data || 0)}</p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <p className="admin-token-tab-title text-xs text-[#0B1A2F]">Generate Alerts</p>
                    <p className="admin-token-tab-copy text-[10px] text-[#5B708B] mt-1">Assigned from form</p>
                    <p className="text-2xl font-black text-[#F97316] mt-1">{Number(tokenSummary.tab_tokens?.generate_alerts || 0)}</p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <p className="admin-token-tab-title text-xs text-[#0B1A2F]">RTC Optimization</p>
                    <p className="admin-token-tab-copy text-[10px] text-[#5B708B] mt-1">Assigned from form</p>
                    <p className="text-2xl font-black text-[#1C4D88] mt-1">{Number(tokenSummary.tab_tokens?.run_rtc_optimization || 0)}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default CompanyDetail;
